import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FolderPlus, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { useCreateProject, useWorkspaceProjects, useWorkspaceLanguages } from '@/lib/api';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';

export function ProjectsTab() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const projects = useWorkspaceProjects(wsId);
  const languages = useWorkspaceLanguages(wsId);
  const toast = useToast();
  const createProject = useCreateProject();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button onClick={() => setCreating(true)} disabled={!languages.isSuccess}>
          <Plus aria-hidden />
          New project
        </Button>
      </div>

      {projects.isLoading && <Spinner label="Loading projects…" />}

      {projects.isError && (
        <p
          role="alert"
          className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
        >
          Failed to load projects: {projects.error.message}
        </p>
      )}

      {projects.isSuccess && projects.data.length === 0 && (
        <EmptyState
          icon={FolderPlus}
          title="No projects in this workspace"
          description="Projects group translation keys for a single product or codebase."
          action={
            <Button onClick={() => setCreating(true)} disabled={!languages.isSuccess}>
              Create the first project
            </Button>
          }
        />
      )}

      {projects.isSuccess && projects.data.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {projects.data.map((p) => (
            <li key={p.id}>
              <Link
                to={`/workspaces/${wsId}/projects/${p.id}`}
                className="block rounded-lg border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/40"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate font-medium">{p.name}</span>
                  <Badge tone="outline">{p.defaultFormat ?? 'po'}</Badge>
                </div>
                <p className="mt-1 truncate text-xs text-muted-foreground">{p.slug}</p>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <Badge tone="primary">{p.sourceLanguageCode}</Badge>
                  {(p.targetLanguageCodes ?? []).slice(0, 6).map((code) => (
                    <Badge key={code}>{code}</Badge>
                  ))}
                  {(p.targetLanguageCodes ?? []).length > 6 && (
                    <Badge>+{(p.targetLanguageCodes ?? []).length - 6}</Badge>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <CreateProjectDialog
        open={creating}
        onClose={() => setCreating(false)}
        availableLanguages={languages.data ?? []}
        pending={createProject.isPending}
        onSubmit={async (input) => {
          try {
            const created = await createProject.mutateAsync({ workspaceID: wsId, ...input });
            toast.show(`Project “${created.name}” created`, 'success');
            setCreating(false);
          } catch (err) {
            toast.show((err as Error).message, 'error');
          }
        }}
      />
    </div>
  );
}
